import {AccountStore} from "../../../../account/store/AccountStore";
import {Observable, Scheduler} from "rxjs";
import {
    delayedAccountSystemPrompt,
    noSubscriptionPrompt,
    openAccountSystemPrompt,
    openAccountQuizSystemPrompt,
    SystemPrompt,
    tempMessageAccountSystemPrompt,
    temporaryAndDelayedAccountSystemPrompt,
    trialExpiringPrompt,
    swiftReadyPrompt,
    maintenancePrompt
} from "../model/SystemPrompt";
import {AccountBriefRecords, brokerAccounts} from "../../../../account/types/AccountBriefRecord";
import {CurrentUserService} from "../../../../services/user/CurrentUserService";
import {CurrentUser} from "../../../../services/user/models/CurrentUser";
import {Branding} from "../../../../types/Branding";
import {BackOffice} from "../../../../types/BackOffice";
import {
    StripeSubscriptionStatus,
    StripeUserSubscriptionRecord
} from "../../../../stripe/models/StripeUserSubscriptionRecord";
import {DateTimeUtil} from "../../../../js/util/DateTimeUtil";
import {RemainingDaysToShowPrompt} from "../components/trial-expiring/TrialExpiringSystemPromptComponent";
import {BrokerKey} from "../../../../types/BrokerKey";

export class SystemPromptStore {

    public static readonly id = "systemPromptStore";

    private readonly prompt: Observable<SystemPrompt | null>;

    /** @ngInject */
    public constructor(private readonly accountStore: AccountStore,
                       private readonly currentUserService: CurrentUserService) {
        this.prompt = this.buildCurrent();
    }

    private buildCurrent(): Observable<SystemPrompt | null> {
        const source = Observable.combineLatest(this.accountStore.accounts,
            this.currentUserService.current);
        return source.map(([accounts, user]) =>
            this.calculatePrompt(accounts, user))
                     .publishReplay(1)
                     .refCount();
    }

    private calculatePrompt(accounts: AccountBriefRecords, user: CurrentUser): SystemPrompt | null {
        const isLivePrices = user.isLivePrices();
        const isAllfin = user.isBranding(Branding.ALLFIN);
        // const isBelmont = user.isBranding(Branding.BELLMONT);
        // const ifFutureAssist = user.isBranding(Branding.FUTURE_ASSIST);
        // const isWaimak = user.isBranding(Branding.WAIMAK);
        // const isOm = user.isBranding(Branding.OPEN_MARKETS);
        const isIv = user.isBranding(Branding.IV);
        const isPhillip = user.isBranding(Branding.TF) && user.backOffice === BackOffice.PHILLIP;
        const hasPassedQuiz = user.hasPassedMarginQuiz();
        const hasMfa = user.isMfaEnabled();

        // TODO: uncomment this to show temporary msg
        // if (isAllfin || isBelmont || ifFutureAssist || isOm) {
        //     return tempMessageAccountSystemPrompt();
        // }
        if ((user.bannerMessage() ?? "").length !== 0) {
            return maintenancePrompt();
        }

        if (user.isClient() &&  ( isPhillip || isAllfin || isIv)) {
            const broker = brokerAccounts(accounts);
            if (broker.length === 0) {  // if fxe user
                if (isPhillip) {
                    if (user.subscriptions.length === 0) {
                        return noSubscriptionPrompt();
                    } else if (this.isTrialExpiring(user.subscriptions) && !user.hasCard) {
                        return trialExpiringPrompt();
                    } else if (hasPassedQuiz && hasMfa && user.hasCard) {
                        return swiftReadyPrompt();
                    } else {
                        return openAccountQuizSystemPrompt();
                    }
                } else {
                    return openAccountSystemPrompt();
                }
            }   else if (!isLivePrices && !isPhillip) {
                return delayedAccountSystemPrompt();
            }   else if (broker.length > 0 && isPhillip && user.subscriptions.length === 0) {
                return noSubscriptionPrompt();
            } else {
                return null;
            }
        } else if (!isLivePrices) {
            return delayedAccountSystemPrompt();
        } else {
            return null;
        }
    }
    private isTrialExpiring(subscriptions: Array<StripeUserSubscriptionRecord>): boolean {
        const trialSub = subscriptions[0];
        const currentDate = new Date();
        const trialEndDate = new Date(trialSub.trialEnds);
        return subscriptions.length === 1 &&
            trialSub.status === StripeSubscriptionStatus.TRIALING &&
            DateTimeUtil.isBetween(currentDate, new Date(trialSub.trialStarted), trialEndDate) &&
            DateTimeUtil.preciseDaysBetween(currentDate, trialEndDate) <= RemainingDaysToShowPrompt;
    }

    private hasNoFundedAccounts(accounts: AccountBriefRecords): boolean {
        return false;
    }

    public get current(): Observable<SystemPrompt | null> {
        return this.prompt.observeOn(Scheduler.async);
    }
}
